// Inline-Editor für Beistelteile beim Auftrag-Anlegen
// Geht als kontrollierte Komponente — Parent hält das Array und postet
// nach dem Auftrag-Insert für jede Zeile.
import { Package, Plus, Trash2 } from 'lucide-react';

export type BeistelteilDraft = {
  bezeichnung: string;
  zeichnungs_nr: string;
  erwartete_stueckzahl: number;
  erwartet_bis: string;
  notes: string;
};

export function emptyBeistelteilDraft(): BeistelteilDraft {
  return {
    bezeichnung: '',
    zeichnungs_nr: '',
    erwartete_stueckzahl: 1,
    erwartet_bis: '',
    notes: '',
  };
}

type Props = {
  items: BeistelteilDraft[];
  onChange: (items: BeistelteilDraft[]) => void;
};

export default function BeistelteileInputs({ items, onChange }: Props) {
  const add = () => onChange([...items, emptyBeistelteilDraft()]);
  const update = <K extends keyof BeistelteilDraft>(idx: number, key: K, val: BeistelteilDraft[K]) => {
    const next = items.map((item, i) =>
      i === idx ? { ...item, [key]: val } : item
    );
    onChange(next);
  };
  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));

  return (
    <section className="border-t border-slate-100 pt-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2">
          <Package className="w-4 h-4 text-blue-600" />
          Beistelteile (Kunden-Beistellungen)
          {items.length > 0 && <span className="text-slate-400 font-normal">({items.length})</span>}
        </h2>
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-blue-600 text-blue-600 rounded hover:bg-blue-50"
        >
          <Plus className="w-3.5 h-3.5" /> Hinzufügen
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-slate-500 bg-slate-50 rounded p-3">
          Falls der Kunde Teile mitliefert (Welle, Motor, Sonderteil …), hier eintragen.
          Wareneingang bucht den Eingang später automatisch.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, idx) => (
            <div
              key={idx}
              className="grid grid-cols-12 gap-2 items-start bg-slate-50 rounded-lg p-3 border border-slate-200"
            >
              <div className="col-span-4">
                <label className="block text-xs text-slate-600 mb-1">Bezeichnung *</label>
                <input
                  value={item.bezeichnung}
                  onChange={e => update(idx, 'bezeichnung', e.target.value)}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="z.B. Welle 60mm"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-slate-600 mb-1">Zeichnungs-Nr.</label>
                <input
                  value={item.zeichnungs_nr}
                  onChange={e => update(idx, 'zeichnungs_nr', e.target.value)}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="col-span-1">
                <label className="block text-xs text-slate-600 mb-1">Stk *</label>
                <input
                  type="number"
                  min={1}
                  value={item.erwartete_stueckzahl}
                  onChange={e => update(idx, 'erwartete_stueckzahl', parseInt(e.target.value) || 1)}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-slate-600 mb-1">Erwartet bis</label>
                <input
                  type="date"
                  value={item.erwartet_bis}
                  onChange={e => update(idx, 'erwartet_bis', e.target.value)}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-slate-600 mb-1">Notiz</label>
                <input
                  value={item.notes}
                  onChange={e => update(idx, 'notes', e.target.value)}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="optional"
                />
              </div>
              <div className="col-span-1 flex items-end justify-end">
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="p-1.5 text-slate-400 hover:text-red-600 mt-5"
                  title="Entfernen"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
